import {
  CurrentStep,
  HtmlFinalStep,
  HtmlStepperFormData,
  HtmlStepperStep,
} from '@superfunnel/types';
import { SetStateAction, useCallback } from 'react';
import { UseHtmlStepperAgentSessionReturn } from '../../../hooks/useHtmlStepperAgentSession';

export interface UseHtmlStepperAgentSessionActionsProps {
  formId: string | null;
  agentSession: UseHtmlStepperAgentSessionReturn;
  formData: HtmlStepperFormData | null;
  rawFormData: HtmlStepperFormData | null;
  updateFormData: (data: SetStateAction<HtmlStepperFormData | null>) => void;
  /** Callback to navigate to a step when a change is clicked */
  onNavigateToStep?: (step: CurrentStep) => void;
}

export interface UseHtmlStepperAgentSessionActionsReturn {
  agentAcceptChange: (changeId: string) => Promise<void>;
  agentAcceptAllChanges: () => Promise<void>;
  agentDiscardChange: (changeId: string) => Promise<void>;
  agentDiscardAllChanges: () => Promise<void>;
  agentChangeClick: (changeId: string) => void;
}

/**
 * Hook to handle HTML Stepper agent session actions like accepting changes
 * and applying them to the form data
 */
export function useHtmlStepperAgentSessionActions({
  formId,
  agentSession,
  formData,
  rawFormData,
  updateFormData,
  onNavigateToStep,
}: UseHtmlStepperAgentSessionActionsProps): UseHtmlStepperAgentSessionActionsReturn {
  /**
   * Apply a single change to the form data
   */
  const applySingleChange = useCallback(
    (changeId: string, change: any) => {
      const sessionData = agentSession.agentSessionData;
      if (!sessionData || !formData) return;

      const agentFormData = sessionData.formData;
      if (!agentFormData) return;

      if (change.entity === 'wrapper') {
        // Apply wrapper changes
        updateFormData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            wrapper: agentFormData.wrapper,
          };
        });
        console.log('🤖 Applied agent wrapper change:', changeId);
      } else if (change.entity === 'step') {
        // Find the step from agent session
        const agentStep = agentFormData.steps?.find(
          (s: HtmlStepperStep) => s.id === change.entityId,
        );

        if (change.type === 'deleted') {
          // Remove the step
          updateFormData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              steps: prev.steps.filter((s) => s.id !== change.entityId),
            };
          });
          console.log('🤖 Deleted step:', changeId);
        } else if (agentStep) {
          // Add or update the step
          updateFormData((prev) => {
            if (!prev) return prev;
            const existingIndex = prev.steps.findIndex(
              (s) => s.id === change.entityId,
            );
            if (existingIndex >= 0) {
              // Update existing
              const newSteps = [...prev.steps];
              newSteps[existingIndex] = agentStep;
              return { ...prev, steps: newSteps };
            } else {
              // Add new
              return { ...prev, steps: [...prev.steps, agentStep] };
            }
          });
          console.log('🤖 Applied agent step change:', changeId);
        }
      } else if (change.entity === 'finalStep') {
        // Find the final step from agent session
        const agentFinalStep = agentFormData.finalSteps?.find(
          (s: HtmlFinalStep) => s.id === change.entityId,
        );

        if (change.type === 'deleted') {
          // Remove the final step
          updateFormData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              finalSteps: prev.finalSteps.filter(
                (s) => s.id !== change.entityId,
              ),
            };
          });
          console.log('🤖 Deleted final step:', changeId);
        } else if (agentFinalStep) {
          // Add or update the final step
          updateFormData((prev) => {
            if (!prev) return prev;
            const existingIndex = prev.finalSteps.findIndex(
              (s) => s.id === change.entityId,
            );
            if (existingIndex >= 0) {
              // Update existing
              const newFinalSteps = [...prev.finalSteps];
              newFinalSteps[existingIndex] = agentFinalStep;
              return { ...prev, finalSteps: newFinalSteps };
            } else {
              // Add new
              return {
                ...prev,
                finalSteps: [...prev.finalSteps, agentFinalStep],
              };
            }
          });
          console.log('🤖 Applied agent final step change:', changeId);
        }
      }
    },
    [agentSession.agentSessionData, formData, updateFormData],
  );

  /**
   * Create reverted form data for Firestore update
   */
  const createRevertedFormData = useCallback(
    (changeId: string, change: any) => {
      const sessionData = agentSession.agentSessionData;
      if (!sessionData) return null;

      // Clone the current form data from session
      const revertedFormData = JSON.parse(JSON.stringify(sessionData.formData));

      if (change.entity === 'wrapper' && rawFormData?.wrapper) {
        revertedFormData.wrapper = rawFormData.wrapper;
        console.log('🤖 Reverted wrapper change:', changeId);
      } else if (change.entity === 'step') {
        if (change.type === 'created') {
          // Remove the created step
          revertedFormData.steps = revertedFormData.steps.filter(
            (s: HtmlStepperStep) => s.id !== change.entityId,
          );
        } else if (change.type === 'modified' || change.type === 'deleted') {
          // Restore original step
          const originalStep = rawFormData?.steps?.find(
            (s) => s.id === change.entityId,
          );
          if (originalStep) {
            const existingIndex = revertedFormData.steps.findIndex(
              (s: HtmlStepperStep) => s.id === change.entityId,
            );
            if (existingIndex >= 0) {
              revertedFormData.steps[existingIndex] = originalStep;
            } else {
              revertedFormData.steps.push(originalStep);
            }
          }
        }
        console.log('🤖 Reverted step change:', changeId);
      } else if (change.entity === 'finalStep') {
        if (change.type === 'created') {
          // Remove the created final step
          revertedFormData.finalSteps = revertedFormData.finalSteps.filter(
            (s: HtmlFinalStep) => s.id !== change.entityId,
          );
        } else if (change.type === 'modified' || change.type === 'deleted') {
          // Restore original final step
          const originalFinalStep = rawFormData?.finalSteps?.find(
            (s) => s.id === change.entityId,
          );
          if (originalFinalStep) {
            const existingIndex = revertedFormData.finalSteps.findIndex(
              (s: HtmlFinalStep) => s.id === change.entityId,
            );
            if (existingIndex >= 0) {
              revertedFormData.finalSteps[existingIndex] = originalFinalStep;
            } else {
              revertedFormData.finalSteps.push(originalFinalStep);
            }
          }
        }
        console.log('🤖 Reverted final step change:', changeId);
      }

      return revertedFormData;
    },
    [agentSession.agentSessionData, rawFormData],
  );

  const agentChangeClick = useCallback(
    (changeId: string) => {
      const change = agentSession.agentSessionData?.changes[changeId];
      if (!change) return;

      // Navigate to the step/entity that was changed
      if (change.entity === 'step') {
        onNavigateToStep?.({ type: 'step', id: change.entityId });
      } else if (change.entity === 'finalStep') {
        onNavigateToStep?.({ type: 'finalStep', id: change.entityId });
      }
      // For wrapper changes, we could navigate to step 1 or just stay on current step
      // since the wrapper is visible on all steps
    },
    [agentSession.agentSessionData?.changes, onNavigateToStep],
  );

  const agentAcceptChange = useCallback(
    async (changeId: string) => {
      if (!formId || !agentSession.agentSessionData) return;

      const sessionData = agentSession.agentSessionData;
      const change = sessionData.changes[changeId];

      // Apply the change to the form data
      applySingleChange(changeId, change);

      // Update the change status to accepted
      await agentSession.agentUpdateChanges([
        { id: changeId, status: 'accepted' },
      ]);
    },
    [formId, agentSession, applySingleChange],
  );

  const agentDiscardChange = useCallback(
    async (changeId: string) => {
      if (!formId || !agentSession.agentSessionData) return;

      const sessionData = agentSession.agentSessionData;
      const change = sessionData.changes[changeId];

      if (change) {
        console.log(
          '🤖 Discarding agent change and reverting to original state:',
          changeId,
        );

        // Create reverted form data
        const revertedFormData = createRevertedFormData(changeId, change);

        if (revertedFormData) {
          // Update Firestore with reverted form data
          await agentSession.agentUpdateFormData(revertedFormData);
        }
      }

      // Update the change status to rejected
      await agentSession.agentUpdateChanges([
        { id: changeId, status: 'rejected' },
      ]);
    },
    [formId, agentSession, createRevertedFormData],
  );

  const agentAcceptAllChanges = useCallback(async () => {
    if (!formId || !agentSession.agentSessionData) return;

    const sessionData = agentSession.agentSessionData;
    const changeUpdates: { id: string; status: 'accepted' }[] = [];

    // Process changes in order: wrapper, then steps (delete, create, modify), then final steps
    const changeOrder = ['deleted', 'created', 'modified'] as const;

    // Process wrapper first
    for (const [changeId, change] of Object.entries(sessionData.changes)) {
      if (change.status === 'pending' && change.entity === 'wrapper') {
        applySingleChange(changeId, change);
        changeUpdates.push({ id: changeId, status: 'accepted' });
      }
    }

    // Then steps
    for (const changeType of changeOrder) {
      for (const [changeId, change] of Object.entries(sessionData.changes)) {
        if (
          change.status === 'pending' &&
          change.entity === 'step' &&
          change.type === changeType
        ) {
          applySingleChange(changeId, change);
          changeUpdates.push({ id: changeId, status: 'accepted' });
        }
      }
    }

    // Then final steps
    for (const changeType of changeOrder) {
      for (const [changeId, change] of Object.entries(sessionData.changes)) {
        if (
          change.status === 'pending' &&
          change.entity === 'finalStep' &&
          change.type === changeType
        ) {
          applySingleChange(changeId, change);
          changeUpdates.push({ id: changeId, status: 'accepted' });
        }
      }
    }

    // Update all changes to accepted status
    if (changeUpdates.length > 0) {
      await agentSession.agentUpdateChanges(changeUpdates);
    }
  }, [formId, agentSession, applySingleChange]);

  const agentDiscardAllChanges = useCallback(async () => {
    if (!formId || !agentSession.agentSessionData) return;

    const sessionData = agentSession.agentSessionData;
    const changeUpdates: { id: string; status: 'rejected' }[] = [];

    console.log(
      '🤖 Discarding all agent changes and reverting to original form data',
    );

    // For discarding all changes, we revert to the original rawFormData
    if (rawFormData) {
      const originalFormData = JSON.parse(JSON.stringify(rawFormData));

      // Update Firestore with original form data
      await agentSession.agentUpdateFormData(originalFormData);
    }

    // Collect all pending changes for rejection
    for (const [changeId, change] of Object.entries(sessionData.changes)) {
      if (change.status === 'pending') {
        changeUpdates.push({ id: changeId, status: 'rejected' });
      }
    }

    // Update all changes to rejected status
    if (changeUpdates.length > 0) {
      await agentSession.agentUpdateChanges(changeUpdates);
    }
  }, [formId, agentSession, rawFormData]);

  return {
    agentAcceptChange,
    agentAcceptAllChanges,
    agentDiscardChange,
    agentDiscardAllChanges,
    agentChangeClick,
  };
}
